/**
 * SEO Mixin
 *
 * This mixin can be used by page components to set route-specific SEO metadata.
 * It generates meta tags based on the current route and any additional data
 * provided by the component.
 */

import { matchesExcludeFromIndex } from "../seo";

export default {
  metaInfo() {
    // Get environment configuration
    const store = this.$store || {};
    const storeState = store.state || {};
    const env = storeState.env || {};
    const countryConfig = env;

    // Default values from country config
    const defaults = {
      title: countryConfig.websiteTitle || '',
      description: countryConfig.websiteDescription || '',
      image: countryConfig.websiteOpenGraphImage || '',
      url: this.getPageUrl()
    };

    // Preserve existing page title/description from original metaInfo if available
    let existingMetaInfo = null;
    if (this.$options && this.$options.metaInfo) {
      if (typeof this.$options.metaInfo === 'function') {
        existingMetaInfo = this.$options.metaInfo.call(this);
      } else {
        existingMetaInfo = this.$options.metaInfo;
      }
    }

    // Find existing meta tags if they exist
    const findMetaContent = function(metaArray, name, property) {
      if (!metaArray || !Array.isArray(metaArray)) return null;

      for (let i = 0; i < metaArray.length; i++) {
        const meta = metaArray[i];
        if (name && meta.name === name) return meta.content;
        if (property && meta.property === property) return meta.content;
      }
      return null;
    };

    // Find existing link if it exists
    const findLinkHref = function(linkArray, rel) {
      if (!linkArray || !Array.isArray(linkArray)) return null;

      for (let i = 0; i < linkArray.length; i++) {
        const link = linkArray[i];
        if (rel && link.rel === rel) return link.href;
      }
      return null;
    };

    const existingMeta = existingMetaInfo && existingMetaInfo.meta ? existingMetaInfo.meta : [];
    const existingLinks = existingMetaInfo && existingMetaInfo.link ? existingMetaInfo.link : [];

    // Component can override these by setting seoTitle, seoDescription, etc.
    const meta = {
      title: this.seoTitle || (existingMetaInfo && existingMetaInfo.title) || defaults.title,
      meta: [
        // Basic meta tags
        {
          vmid: 'description',
          name: 'description',
          content: this.seoDescription ||
                  findMetaContent(existingMeta, 'description', null) ||
                  defaults.description
        },
        // Open Graph tags for social sharing
        {
          vmid: 'og:title',
          property: 'og:title',
          content: this.seoTitle ||
                  findMetaContent(existingMeta, null, 'og:title') ||
                  defaults.title
        },
        {
          vmid: 'og:description',
          property: 'og:description',
          content: this.seoDescription ||
                  findMetaContent(existingMeta, null, 'og:description') ||
                  defaults.description
        },
        {
          vmid: 'og:image',
          property: 'og:image',
          content: this.seoImage ||
                  findMetaContent(existingMeta, null, 'og:image') ||
                  defaults.image
        },
        {
          vmid: 'og:url',
          property: 'og:url',
          content: this.seoUrl ||
                  findMetaContent(existingMeta, null, 'og:url') ||
                  defaults.url
        },
        {
          vmid: 'og:type',
          property: 'og:type',
          content: this.seoType ||
                  findMetaContent(existingMeta, null, 'og:type') ||
                  'website'
        },
        // Twitter card tags
        {
          vmid: 'twitter:card',
          name: 'twitter:card',
          content: findMetaContent(existingMeta, 'twitter:card', null) ||
                  'summary_large_image'
        },
        {
          vmid: 'twitter:title',
          name: 'twitter:title',
          content: this.seoTitle ||
                  findMetaContent(existingMeta, 'twitter:title', null) ||
                  defaults.title
        },
        {
          vmid: 'twitter:description',
          name: 'twitter:description',
          content: this.seoDescription ||
                  findMetaContent(existingMeta, 'twitter:description', null) ||
                  defaults.description
        },
        {
          vmid: 'twitter:image',
          name: 'twitter:image',
          content: this.seoImage ||
                  findMetaContent(existingMeta, 'twitter:image', null) ||
                  defaults.image
        }
      ],
      link: [
        {
          rel: 'canonical',
          href: this.seoUrl ||
                findLinkHref(existingLinks, 'canonical') ||
                defaults.url
        }
      ]
    };

    // Add structured data if the component provides it
    if (this.structuredData) {
      meta.script = meta.script || [];
      meta.script.push({
        type: 'application/ld+json',
        json: this.structuredData
      });
    }

    // Add hreflang tags for multi-language support if the component provides them
    if (this.hreflangUrls && Array.isArray(this.hreflangUrls)) {
      for (let i = 0; i < this.hreflangUrls.length; i++) {
        const item = this.hreflangUrls[i];
        if (item.lang && item.url) {
          meta.link.push({
            rel: 'alternate',
            hreflang: item.lang,
            href: item.url
          });
        }
      }
    }

    // If the component has existing meta tags that we haven't processed, add them
    if (existingMetaInfo && existingMetaInfo.meta) {
      const filteredExistingMeta = [];
      for (let i = 0; i < existingMeta.length; i++) {
        const m = existingMeta[i];
        // Only include meta tags that we haven't already processed
        if (m.vmid && !meta.meta.some(function(newM) { return newM.vmid === m.vmid; })) {
          filteredExistingMeta.push(m);
        }
      }

      if (filteredExistingMeta.length > 0) {
        for (let i = 0; i < filteredExistingMeta.length; i++) {
          meta.meta.push(filteredExistingMeta[i]);
        }
      }
    }

    return meta;
  },

  methods: {
    /**
     * Gets the full URL for the current page
     */
    getPageUrl() {
      if (!this.$route) return '';

      const store = this.$store || {};
      const storeState = store.state || {};
      const env = storeState.env || {};
      const baseUrl = env.websiteUrl || '';
      const path = this.$route.fullPath || '';

      // If baseUrl already has the path, don't duplicate it
      if (baseUrl.endsWith(path)) return baseUrl;

      // Ensure there's only one slash between base and path
      const base = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      const routePath = path.startsWith('/') ? path : '/' + path;

      return base + routePath;
    },

    /**
     * Check if the current route should be excluded from search engines
     * Based on the SEO configuration
     */
    shouldExcludeFromIndex() {
      if (!this.$route || !this.$store) return false;

      const store = this.$store || {};
      const storeState = store.state || {};
      const env = storeState.env || {};

      if (!env.seoConfiguration || !env.seoConfiguration.excludeFromIndex) return false;

      return matchesExcludeFromIndex(this.$route.path, env.seoConfiguration.excludeFromIndex);
    }
  },

  created() {
    // Auto-set noIndex based on SEO configuration if not explicitly set by component
    if (this.shouldExcludeFromIndex() && this.noIndex === undefined && this.seoNoIndex === undefined) {
      this.noIndex = true;
    }
  }
};
